import React from 'react';
import { Bar } from 'react-chartjs-2';
import '../lib/charts';
import { SERIES } from '../lib/charts';
import useReport from '../lib/useReport';
import { useFilters } from '../contexts/FilterContext';
import { money, moneyCompact, int, pct, niceDate } from '../lib/format';
import { Card, KpiCard, Spinner, ErrorBox, Empty, DataTable } from '../components/ui';

// Customer retention & frequency (#4). Chain-wide by default; respects the
// location filter (per-store retention). Only orders carrying a customer_id
// count — POS walk-in cash orders without one are excluded.
export default function RetentionPage() {
  const { reportParams, startDate, endDate } = useFilters();

  const ret = useReport('customer-retention', reportParams);
  const cohorts = useReport('customer-cohorts', reportParams);
  const lapsed = useReport('lapsed-customers', { ...(reportParams.locations ? { locations: reportParams.locations } : {}) });

  return (
    <div className="grid" style={{ gap: 14 }}>
      {ret.error ? <ErrorBox message={ret.error} /> :
        ret.loading || !ret.data ? <Spinner label="Analyzing customer behavior…" /> : (
          <RetentionTop data={ret.data} startDate={startDate} endDate={endDate} />
        )}

      <div className="grid two-col">
        <Card tour="ret-cohorts" title="New Customer Repeat Rate" sub="of each month's first-time customers, the share who came back within 90 days · trailing 12 months">
          {cohorts.error ? <ErrorBox message={cohorts.error} /> :
            cohorts.loading || !cohorts.data ? <Spinner /> :
            cohorts.data.length === 0 ? <Empty /> : <CohortChart rows={cohorts.data} />}
        </Card>

        <Card tour="ret-freq" title="Customer Frequency" sub="active customers this period by lifetime order count">
          {ret.loading || !ret.data ? <Spinner /> : <FrequencyChart segments={ret.data.segments} />}
        </Card>
      </div>

      <LapsedCard state={lapsed} />
    </div>
  );
}

function RetentionTop({ data, startDate, endDate }) {
  const { activeCustomers, newCustomers, returningCustomers, newNet, returningNet, avgOrders } = data;
  const repeatRate = activeCustomers ? returningCustomers / activeCustomers : 0;
  const totalNet = newNet + returningNet;
  const returningRevShare = totalNet ? returningNet / totalNet : 0;

  return (
    <>
      <div className="grid kpi-grid">
        <KpiCard label="Active Customers" value={int(activeCustomers)} sub={`${niceDate(startDate)} – ${niceDate(endDate)}`} />
        <KpiCard label="Returning Rate" value={pct(repeatRate)} sub={`${int(returningCustomers)} returning · ${int(newCustomers)} new`} />
        <KpiCard label="Returning Revenue" value={pct(returningRevShare)} sub={`${moneyCompact(returningNet)} of ${moneyCompact(totalNet)}`} />
        <KpiCard label="Avg Orders / Customer" value={avgOrders.toFixed(2)} sub="in this period" />
        <KpiCard label="New Customers" value={int(newCustomers)} sub={`${moneyCompact(newNet)} in sales`} />
      </div>

      <Card tour="ret-split" title="Where Revenue Comes From" sub="new (first order in this period) vs returning customers">
        <RevenueSplit newNet={newNet} returningNet={returningNet} newCount={newCustomers} returningCount={returningCustomers} />
      </Card>
    </>
  );
}

function RevenueSplit({ newNet, returningNet, newCount, returningCount }) {
  const total = newNet + returningNet;
  if (!total) return <Empty />;
  const rShare = (returningNet / total) * 100;
  const seg = (label, value, count, color, amount) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, fontSize: 13 }}>
      <span style={{ width: 10, height: 10, borderRadius: 2, background: color, flexShrink: 0 }} />
      <span style={{ width: 90, fontWeight: 600 }}>{label}</span>
      <span style={{ color: 'var(--ink-2)' }}>{money(amount)} · {int(count)} customers</span>
      <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums', color: 'var(--ink-muted)' }}>{pct(amount / total)}</span>
    </div>
  );
  return (
    <div>
      <div style={{ display: 'flex', height: 34, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
        <div style={{ width: `${rShare}%`, background: SERIES[0], display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 700 }}>
          {rShare > 12 ? `Returning ${rShare.toFixed(0)}%` : ''}
        </div>
        <div style={{ width: `${100 - rShare}%`, background: SERIES[2], display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 700 }}>
          {100 - rShare > 12 ? `New ${(100 - rShare).toFixed(0)}%` : ''}
        </div>
      </div>
      {seg('Returning', rShare, returningCount, SERIES[0], returningNet)}
      {seg('New', 100 - rShare, newCount, SERIES[2], newNet)}
      <div className="legend-note">Returning customers almost always drive the majority of revenue — protecting that base (see lapsed regulars below) usually beats chasing new covers.</div>
    </div>
  );
}

function CohortChart({ rows }) {
  const data = {
    labels: rows.map(r => r.cohort),
    datasets: [{
      label: '90-day repeat rate',
      data: rows.map(r => r.newCustomers ? Math.round((r.repeat90 / r.newCustomers) * 1000) / 10 : 0),
      backgroundColor: SERIES[1],
      borderRadius: 4,
      maxBarThickness: 34,
    }],
  };
  return (
    <div className="chart-box">
      <Bar
        data={data}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: ctx => {
                  const r = rows[ctx.dataIndex];
                  return [` ${ctx.parsed.y}% came back within 90 days`, ` ${int(r.repeat90)} of ${int(r.newCustomers)} new customers`];
                },
                afterlabel: ctx => {
                  const r = rows[ctx.dataIndex];
                  return `  30d ${r.newCustomers ? ((r.repeat30 / r.newCustomers) * 100).toFixed(0) : 0}% · 60d ${r.newCustomers ? ((r.repeat60 / r.newCustomers) * 100).toFixed(0) : 0}%`;
                },
              },
            },
          },
          scales: {
            y: { beginAtZero: true, ticks: { callback: v => `${v}%` } },
            x: { grid: { display: false } },
          },
        }}
      />
      <div className="legend-note">The most recent months look low only because their customers haven't had a full 90 days yet.</div>
    </div>
  );
}

function FrequencyChart({ segments }) {
  const { oneTime, occasional, loyal } = segments;
  const total = oneTime + occasional + loyal || 1;
  const data = {
    labels: ['One-time', 'Occasional (2–4)', 'Loyal (5+)'],
    datasets: [{
      data: [oneTime, occasional, loyal],
      backgroundColor: [SERIES[5], SERIES[2], SERIES[1]],
      borderRadius: 4,
      maxBarThickness: 40,
    }],
  };
  return (
    <div className="chart-box">
      <Bar
        data={data}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: ctx => ` ${int(ctx.parsed.y)} customers (${((ctx.parsed.y / total) * 100).toFixed(1)}%)` } },
          },
          scales: {
            y: { beginAtZero: true, ticks: { callback: int } },
            x: { grid: { display: false } },
          },
        }}
      />
      <div className="legend-note">By lifetime order count. Moving one-timers into the occasional band is the biggest retention lever.</div>
    </div>
  );
}

function LapsedCard({ state }) {
  const { data, loading, error } = state;
  return (
    <Card
      tour="ret-lapsed"
      title="Lapsed Regulars — Win-Back List"
      sub={data?.asOf ? `customers with 3+ orders whose last visit was 60–180 days before ${niceDate(data.asOf)}` : 'customers who used to order regularly and have gone quiet'}
    >
      {error ? <ErrorBox message={error} /> :
        loading || !data ? <Spinner label="Finding customers who've gone quiet…" /> :
        data.lapsedCount === 0 ? <Empty>No lapsed regulars in range. 🎉</Empty> : (
          <>
            <div className="grid kpi-grid" style={{ marginBottom: 12 }}>
              <KpiCard label="Lapsed Regulars" value={int(data.lapsedCount)} sub="3+ orders, quiet 60–180 days" />
              <KpiCard label="Lifetime Value at Risk" value={moneyCompact(data.atRiskValue)} sub="total historical spend of this group" />
              <KpiCard label="Avg Value Each" value={money(Math.round(data.atRiskValue / data.lapsedCount))} sub="lifetime, per customer" />
            </div>
            <DataTable
              csvName="lapsed-regulars-winback.csv"
              defaultSort={{ key: 'lifetimeValue', dir: 'desc' }}
              maxRows={20}
              columns={[
                { key: 'name', label: 'Customer' },
                { key: 'phone', label: 'Phone', render: r => r.phone || '—' },
                { key: 'email', label: 'Email', render: r => r.email || '—' },
                { key: 'orders', label: 'Orders', num: true, render: r => int(r.orders) },
                { key: 'lifetimeValue', label: 'Lifetime Value', num: true, render: r => money(r.lifetimeValue), csv: r => (r.lifetimeValue / 100).toFixed(2) },
                { key: 'lastOrder', label: 'Last Order', render: r => niceDate(r.lastOrder) },
              ]}
              rows={data.customers.map(c => ({ ...c, __key: c.customerId }))}
            />
            <div className="legend-note">
              Top 200 by lifetime value shown; CSV exports the same. Guest/placeholder accounts are listed without contact details.
            </div>
          </>
        )}
    </Card>
  );
}
